import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import HeaderDashboard from '../header/headerDashboard';
import { EditBedForm } from '../forms/beds_edit';
import { markAsClean, fetchCleaningLogs } from '../../actions/cleaning';
import { addMaintenanceLog, fetchMaintenanceLogs } from '../../actions/maintenance';

const STATUS_LABELS = {
    DISPONIBLE:  { label: 'Disponible',  color: '#27ae60' },
    EN_LOCATION: { label: 'En location', color: '#2980b9' },
    SALE:        { label: 'Sale',        color: '#c0392b' },
};

const BedStatus = ({ status }) => {
    const cfg = STATUS_LABELS[status] || STATUS_LABELS.DISPONIBLE;
    return (
        <span style={{
            display: 'inline-block',
            padding: '3px 10px',
            borderRadius: 4,
            backgroundColor: cfg.color,
            color: '#fff',
            fontWeight: 'bold',
            fontSize: 13,
            marginLeft: 10,
        }}>
            {cfg.label}
        </span>
    );
};

const formatDate = (date) => new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
});

const CleaningHistory = ({ logs }) => {
    if (!logs.length) {
        return <p style={{ color: '#888', fontStyle: 'italic' }}>Aucun nettoyage enregistré.</p>;
    }
    return (
        <ul style={{ listStyle: 'none', padding: 0 }}>
            {logs.map(log => (
                <li key={log._id} style={{
                    borderLeft: '3px solid #27ae60',
                    padding: '6px 12px',
                    marginBottom: 8,
                    backgroundColor: '#f9f9f9',
                    borderRadius: 4,
                }}>
                    <strong>{formatDate(log.cleaningDate)}</strong>
                    {' — '}
                    <span style={{ color: '#555' }}>Nettoyage / désinfection</span>
                </li>
            ))}
        </ul>
    );
};

const MaintenanceHistory = ({ logs }) => {
    if (!logs.length) {
        return <p style={{ color: '#888', fontStyle: 'italic' }}>Aucune maintenance enregistrée.</p>;
    }
    return (
        <ul style={{ listStyle: 'none', padding: 0 }}>
            {logs.map(log => (
                <li key={log._id} style={{
                    borderLeft: `3px solid ${log.type === 'PREVENTIVE' ? '#2980b9' : '#e67e22'}`,
                    padding: '6px 12px',
                    marginBottom: 8,
                    backgroundColor: '#f9f9f9',
                    borderRadius: 4,
                }}>
                    <strong>{formatDate(log.maintenanceDate)}</strong>
                    {' — '}
                    <span style={{
                        fontWeight: 'bold',
                        color: log.type === 'PREVENTIVE' ? '#2980b9' : '#e67e22',
                        marginRight: 6,
                    }}>
                        {log.type === 'PREVENTIVE' ? 'Préventive' : 'Corrective'}
                    </span>
                    {'— '}
                    <span style={{ color: '#555' }}>{log.description}</span>
                </li>
            ))}
        </ul>
    );
};

const MaintenanceForm = ({ bedNumber, onSuccess }) => {
    const [type, setType] = useState('PREVENTIVE');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!description.trim()) return;
        setLoading(true);
        const success = await addMaintenanceLog(bedNumber, type, description);
        if (success) {
            setDescription('');
            setType('PREVENTIVE');
            setOpen(false);
            onSuccess();
        }
        setLoading(false);
    };

    if (!open) {
        return (
            <button
                className="btn btn-outline-warning btn-sm"
                onClick={() => setOpen(true)}
                style={{ marginBottom: 12 }}
            >
                + Ajouter une maintenance
            </button>
        );
    }

    return (
        <form onSubmit={handleSubmit} style={{
            backgroundColor: '#fffbea',
            border: '1px solid #f0c040',
            borderRadius: 6,
            padding: '14px 16px',
            marginBottom: 16,
        }}>
            <div style={{ marginBottom: 10 }}>
                <label style={{ fontWeight: 'bold', fontSize: 13, display: 'block', marginBottom: 4 }}>
                    Type de maintenance
                </label>
                <div style={{ display: 'flex', gap: 10 }}>
                    {[
                        { value: 'PREVENTIVE', label: 'Préventive', color: '#2980b9' },
                        { value: 'CORRECTIVE', label: 'Corrective', color: '#e67e22' },
                    ].map(opt => (
                        <label key={opt.value} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            cursor: 'pointer',
                            padding: '6px 14px',
                            borderRadius: 20,
                            border: `2px solid ${type === opt.value ? opt.color : '#ddd'}`,
                            backgroundColor: type === opt.value ? opt.color + '18' : '#fff',
                            fontWeight: type === opt.value ? 'bold' : 'normal',
                            fontSize: 13,
                            color: type === opt.value ? opt.color : '#555',
                        }}>
                            <input
                                type="radio"
                                name="maintenance-type"
                                value={opt.value}
                                checked={type === opt.value}
                                onChange={() => setType(opt.value)}
                                style={{ display: 'none' }}
                            />
                            {opt.label}
                        </label>
                    ))}
                </div>
            </div>

            <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 'bold', fontSize: 13, display: 'block', marginBottom: 4 }}>
                    Description
                </label>
                <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="ex : Contrôle annuel – vérification moteurs OK"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    required
                />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
                <button
                    type="submit"
                    className="btn btn-warning btn-sm"
                    disabled={loading || !description.trim()}
                >
                    {loading ? 'Enregistrement...' : 'Enregistrer'}
                </button>
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setOpen(false)}
                >
                    Annuler
                </button>
            </div>
        </form>
    );
};

const Bed = (props) => {
    const [bed, setBed] = useState({});
    const [cleaningLogs, setCleaningLogs] = useState([]);
    const [maintenanceLogs, setMaintenanceLogs] = useState([]);
    const [loading, setLoading] = useState(false);

    const bedNumber = props.match.params.number;

    const loadBed = () => {
        fetch(`${process.env.REACT_APP_API_URL}/beds/${bedNumber}`, {
            method: 'GET',
            credentials: 'include',
            headers: new Headers({ 'Content-Type': 'application/json' })
        })
        .then(async res => {
            if (res.status === 200) return res.json();
            throw await res.json();
        })
        .then(res => setBed(res))
        .catch(err => console.error(err));
    };

    const loadLogs = () => {
        fetchCleaningLogs(bedNumber).then(data => setCleaningLogs(data));
        fetchMaintenanceLogs(bedNumber).then(data => setMaintenanceLogs(data));
    };

    useEffect(() => {
        loadBed();
        loadLogs();
    }, []);

    const handleMarkAsClean = async () => {
        setLoading(true);
        const success = await markAsClean(bedNumber);
        if (success) {
            loadBed();
            loadLogs();
        }
        setLoading(false);
    };

    const bedStatus = bed.status || (bed.isRent ? 'EN_LOCATION' : 'DISPONIBLE');

    return (
        <div>
            <HeaderDashboard />
            <Container style={{ marginTop: 20 }}>
                <h1>
                    Lit numéro : {bed.number}
                    <BedStatus status={bedStatus} />
                </h1>

                {bedStatus === 'SALE' && (
                    <div style={{
                        backgroundColor: '#fdf3f2',
                        border: '1px solid #e74c3c',
                        borderRadius: 4,
                        padding: '12px 16px',
                        marginBottom: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}>
                        <span style={{ color: '#c0392b', fontWeight: 'bold' }}>
                            ⚠ Ce lit nécessite un nettoyage avant d'être remis en location.
                        </span>
                        <button
                            className="btn btn-success"
                            onClick={handleMarkAsClean}
                            disabled={loading}
                            style={{ marginLeft: 16, whiteSpace: 'nowrap' }}
                        >
                            {loading ? 'Enregistrement...' : 'Marquer comme nettoyé'}
                        </button>
                    </div>
                )}

                <EditBedForm bed={bed} />

                <hr style={{ marginTop: 32 }} />
                <h4>Historique des nettoyages</h4>
                <CleaningHistory logs={cleaningLogs} />

                <hr style={{ marginTop: 32 }} />
                <h4>Historique des maintenances</h4>
                <MaintenanceForm bedNumber={bedNumber} onSuccess={loadLogs} />
                <MaintenanceHistory logs={maintenanceLogs} />
            </Container>
        </div>
    );
};

export default Bed;
